import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideBell, lucideBellOff, lucideUsers, lucideX } from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmScrollAreaImports } from '@spartan-ng/helm/scroll-area';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { NgScrollbar } from 'ngx-scrollbar';
import {
  CompanyNotification,
  CompanyNotificationService,
} from '../../../../services/company-notification.service';
import { SetupProgressService } from '../../../../services/setup-progress.service';
import { SetupProgressComponent } from '../setup-progress/setup-progress.component';

/**
 * Notification bell with a popover that combines unread notifications and the
 * onboarding checklist. Rendered once and reused on all breakpoints.
 */
@Component({
  selector: 'app-notification-menu',
  imports: [
    NgIcon,
    NgScrollbar,
    HlmBadgeImports,
    HlmButtonImports,
    HlmEmptyImports,
    HlmItemImports,
    HlmPopoverImports,
    HlmScrollAreaImports,
    HlmSeparatorImports,
    SetupProgressComponent,
  ],
  providers: [provideIcons({ lucideBell, lucideBellOff, lucideUsers, lucideX })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-popover align="center" [state]="state()" (stateChanged)="state.set($event)">
      <button
        hlmPopoverTrigger
        hlmBtn
        variant="ghost"
        size="icon"
        class="relative"
        aria-label="Benachrichtigungen & Aufgaben">
        <ng-icon name="lucideBell" />
        @if (indicator(); as indicator) {
          <span hlmBadge [variant]="indicator.variant" class="absolute -end-1.5 -top-1.5">
            {{ indicator.label }}
          </span>
        }
      </button>

      <hlm-popover-content *hlmPopoverPortal class="w-80 gap-0 p-0" aria-label="Benachrichtigungen">
        <div hlmPopoverHeader class="flex-row items-center justify-between gap-2 p-2.5">
          <h3 hlmPopoverTitle>Benachrichtigungen & Aufgaben</h3>
          @if (unreadCount() > 0) {
            <button hlmBtn variant="link" size="xs" class="px-0" (click)="markAllRead()">
              Alle gelesen
            </button>
          }
        </div>

        <div hlmSeparator></div>

        <ng-scrollbar hlm class="max-h-56">
          <div hlmItemGroup class="p-1.5">
            @for (notification of notifications(); track notification.id) {
              <!-- The title button stretches over the whole item (after:inset-0), so the
                   item stays clickable while the close button sits on top of it. -->
              <div
                hlmItem
                size="sm"
                [variant]="notification.is_read ? 'default' : 'muted'"
                class="hover:bg-accent/50 relative">
                <div hlmItemMedia variant="icon">
                  <ng-icon name="lucideUsers" />
                </div>
                <div hlmItemContent>
                  <button
                    hlmItemTitle
                    type="button"
                    class="text-start outline-none after:absolute after:inset-0 focus-visible:underline"
                    (click)="open(notification)">
                    {{ notification.title }}
                  </button>
                  <span hlmItemDescription>{{ notification.message }}</span>
                  <span hlmItemDescription>{{ relativeTime(notification.created_at) }}</span>
                </div>
                <div hlmItemActions class="relative z-10 self-start">
                  <button
                    hlmBtn
                    variant="ghost"
                    size="icon-xs"
                    class="text-muted-foreground"
                    aria-label="Benachrichtigung schließen"
                    title="Schließen"
                    (click)="close(notification)">
                    <ng-icon name="lucideX" />
                  </button>
                </div>
              </div>
            } @empty {
              <hlm-empty class="border-none py-4">
                <div hlmEmptyHeader>
                  <div hlmEmptyMedia variant="icon">
                    <ng-icon name="lucideBellOff" />
                  </div>
                  <p hlmEmptyTitle>Keine neuen Nachrichten</p>
                </div>
              </hlm-empty>
            }
          </div>
        </ng-scrollbar>

        <div hlmSeparator></div>

        <div class="p-2.5">
          <div class="mb-1.5 flex items-center justify-between gap-2">
            <h3 hlmPopoverTitle>Einrichtung</h3>
            @if (openTaskCount() > 0) {
              <span hlmBadge variant="secondary">{{ openTaskCount() }} offen</span>
            }
          </div>
          <app-setup-progress (navigated)="state.set('closed')" />
        </div>
      </hlm-popover-content>
    </hlm-popover>
  `,
})
export class NotificationMenuComponent {
  private readonly notifService = inject(CompanyNotificationService);
  private readonly setup = inject(SetupProgressService);
  private readonly router = inject(Router);

  protected readonly state = signal<'open' | 'closed'>('closed');

  protected readonly notifications = this.notifService.notifications;
  protected readonly unreadCount = this.notifService.unreadCount;

  /** Open setup tasks — the same number the bell shows when nothing is unread. */
  protected readonly openTaskCount = computed(() =>
    this.setup.requiredPhase()
      ? this.setup.activeRequiredTasks().length
      : this.setup.pendingCount(),
  );

  /** Unread notifications outrank required tasks, which outrank optional ones. */
  protected readonly indicator = computed(() => {
    const unread = this.unreadCount();
    if (unread > 0) {
      return { variant: 'destructive' as const, label: unread > 9 ? '9+' : `${unread}` };
    }
    const openTasks = this.openTaskCount();
    if (openTasks > 0) {
      return {
        variant: this.setup.requiredPhase() ? ('default' as const) : ('secondary' as const),
        label: `${openTasks}`,
      };
    }
    return null;
  });

  protected markAllRead(): void {
    this.notifService.markAllRead().subscribe();
  }

  protected close(notification: CompanyNotification): void {
    this.notifService.remove(notification.id).subscribe();
  }

  protected open(notification: CompanyNotification): void {
    this.notifService.markRead(notification.id).subscribe();
    this.state.set('closed');
    if (notification.type === 'member_settlement_paid') {
      this.router.navigate(['/payouts']);
      return;
    }
    this.router.navigate(['/settings'], { queryParams: { tab: 'verbund-members' } });
  }

  protected relativeTime(dateStr: string): string {
    const minutes = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60_000);
    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min.`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    const days = Math.floor(hours / 24);
    return `vor ${days} Tag${days === 1 ? '' : 'en'}`;
  }
}
